/**
 * Quick View component
 * 
 * @module quickview
 * @version 1.0.0
 * @extends HTMLElement
 */

customElements.component = 'component-quickview';

if(!customElements.get(customElements.component)) {

    class quickView extends HTMLElement {

        /**
         * Quick view web component
         * 
         * @constructor
         */
        constructor() {
    
            super();
    
            console.log('[QuickView] Initializing component');
            this.initEvents();
            this.initFocusTrap();
            // Flags to track if listeners have been added
            this._variantChangeListenerAdded = false;
            this._cartUpdateListenerAdded = false;
            // Initialize product inventory data property
            this.productInventoryQuantity = null;
            this.slider = null;
            // Add debounce mechanism for variant changes
            this._debounceTimer = null;
            this._pendingVariantChange = null;
            // Add flag to track if swatch inputs are disabled
            this._swatchInputsDisabled = false;
            // Add timer for swatch input delay
            this._swatchInputDelayTimer = null;
            // Track the current product ID to handle multiple quickviews
            this.currentProductId = null;

            window.addEventListener('init-quickview', event => {
                console.log('[QuickView] Received init-quickview event');
                this.initEvents();
                this.initFocusTrap();
            });
            
            window.theme.quickView = this;
            this.activeVariant = null;
            console.log('[QuickView] Component initialized');
        }

        /**
         * Init component events.
         * 
         * @returns {undefined}
         */
        initEvents() {
            this.initOpenEvents();
            this.initCloseEvents();
        }

        /**
         * Init quick view open events
         * 
         * @returns {undefined}
         */
        initOpenEvents() {
            let btnsOpen = document.querySelectorAll('[data-quickview-handle]');

            btnsOpen.forEach(btn => {
                if(!btn.classList.contains('js-active')) {
                    btn.classList.add('js-active');
                    btn.addEventListener('click', event => {
                        event.preventDefault();
                        this.open(btn.dataset.quickViewHandle);
                    });
                }
            });
        }

        /**
         * Init quick view open events
         * 
         * @returns {undefined}
         */
        initCloseEvents() {
            this.querySelectorAll('[data-quickview-close]').forEach(element => {
                element.addEventListener('click', event => {
                    event.preventDefault();
                    this.close();
                });
            });
        }

         /**
         * Init the product slider 
         * 
         * @returns {undefined}
         */
          initSlider() {
            console.log('[QuickView] Initializing slider');
            let slider = this.querySelector('[data-slide-init]');

            if(slider && this.enable_carousel) {
                console.log('[QuickView] Slider container found, enable_carousel=true');
                
                // Check if slider instance already exists
                if (this.slider) {
                    console.log('[QuickView] Existing slider found, destroying it first');
                    try {
                        this.slider.destroy();
                    } catch (error) {
                        console.error('[QuickView] Error destroying existing slider:', error);
                    }
                    this.slider = null;
                }
                
                console.log('[QuickView] Creating new Swiper instance');
                try {
                    this._queuedSliderSync = null; // clear any previous queued sync
                    this.slider = new Swiper(slider, {
                        preloadImages: true,
                        slidesPerView: 2,
                        spaceBetween: 13,
                        lazy: {
                            loadPrevNext: true,
                        },
                        autoHeight: true,
                        navigation: {
                            nextEl: '.swiper-button-next',
                            prevEl: '.swiper-button-prev',
                        },
                        breakpoints: {
                            768: {
                                slidesPerView: 1
                            }
                        },
                        on: {
                            init: () => {
                                console.log('[QuickView] Swiper initialized successfully');
                                this.classList.add("active-slider");
                                this.classList.add("loaded");
                                // If a queued image sync exists, run it now
                                if (typeof this._queuedSliderSync === 'function') {
                                        this._queuedSliderSync();
                                        this._queuedSliderSync = null;
                                }
                            },
                            slideChange: index => {
                                console.log(`[QuickView] Slide changed to index: ${index}`);
                            }
                        }
                    });
                    console.log('[QuickView] Slider initialization complete, instance:', this.slider ? 'created' : 'failed');
                } catch (error) {
                    console.error('[QuickView] Error creating Swiper instance:', error);
                    this.slider = null;
                    this.classList.add("loaded");
                }
            } else {
                console.log('[QuickView] No slider container found or carousel disabled, adding loaded class only');
                this.classList.add("loaded");
            }
        }

        /**
         * Destroy the product slider 
         *  
         * @returns {undefined}
         */
        destroySlider() {
            console.log('[QuickView] Attempting to destroy slider');
            if (this.slider && this.enable_carousel) {
                console.log('[QuickView] Valid slider instance found, destroying it');
                try {
                    this.slider.destroy();
                    console.log('[QuickView] Slider successfully destroyed');
                } catch (error) {
                    console.error('[QuickView] Error destroying slider:', error);
                }
                // Clear the swiper-wrapper DOM to remove old slides
                const slider = this.querySelector('[data-slide-init]');
                if (slider) {
                    const wrapper = slider.querySelector('.swiper-wrapper');
                    if (wrapper) wrapper.innerHTML = '';
                }
                this.slider = null;
                this.enable_carousel = false;
                this.classList.remove("active-slider");
                console.log('[QuickView] Slider reference cleared, carousel disabled');
            } else {
                console.log('[QuickView] No slider to destroy or carousel already disabled');
            }
        }

        /**
         * Open quick view
         * 
         * @param {string} handle Product handle.
         * @param {string} [variantId] Optional variant id to preselect.
         * @returns {undefined}
         */
        open(handle, variantId) {
            console.log(`[QuickView] Opening quick view for product: ${handle}`);
            document.querySelector('body').classList.add('quickview-active');
            document.querySelector('.quickview__overlay').classList.remove('hidden');

            window.theme.product.loadProduct(handle, true).then(product => {
                console.log(`[QuickView] Product data loaded:`, product.id, product.title);
                // Store the current product ID
                this.currentProductId = product.id;
                console.log(`[QuickView] Set current product ID to: ${this.currentProductId}`);
                let quickView = document.querySelector('[data-quickview-container]');
                let content = this.loadContent(product);
                console.log(`[QuickView] Content loaded, length: ${content.length}`);
                quickView.innerHTML += content;
                this.initSlider();
                this.initCloseEvents();
                // Fetch inventory data if needed
                console.log("[QuickView] Fetching inventory data for product in quick view");
                this.fetchProductInventory(product).then(inventoryData => {
                    // Store the inventory data locally for this quick view
                    this.productInventoryQuantity = inventoryData;
                    console.log(`[QuickView] Inventory data received, items: ${inventoryData ? inventoryData.length : 0}`);
                    this.continueQuickViewInit(product, variantId);
                });
            });
        }

        /**
         * Get selected variant from the form
         * 
         * @param {object} product Product data.
         * @returns {object} The selected variant.
         */
        getSelectedVariant(product) {
            const variantSelector = this.querySelector('variant-selector[data-handle="'+product.handle+'"] select');
            if (!variantSelector) return null;
            
            const variantId = variantSelector.value;
            return product.variants.find(v => v.id == variantId);
        }

        /**
         * Reset inventory UI elements
         */
        resetInventoryUI() {
            const allAddedMessage = this.querySelector('.js-in-stock-all-added');
            const inStockMessage = this.querySelector('.js-in-stock');
            const addToCartBtn = this.querySelector('.product-addtocart__button');
            
            if (allAddedMessage) {
                allAddedMessage.classList.add('hidden');
            }
            
            if (inStockMessage) {
                // Only hide the message if it doesn't have a quantity value yet
                const quantityElement = inStockMessage.querySelector('.js-in-stock-quantity');
                if (!quantityElement || !quantityElement.textContent.trim()) {
                    inStockMessage.classList.add('hidden');
                }
            }
            
            if (addToCartBtn) {
                addToCartBtn.removeAttribute('disabled');
                addToCartBtn.classList.remove('disabled');
            }
        }

        /**
         * Disable all swatch inputs in the quick view
         * 
         * @returns {undefined}
         */
        disableSwatchInputs() {
            if (this._swatchInputsDisabled) return;
            
            this._swatchInputsDisabled = true;
            
            // Find all swatch inputs in the quick view, but only disable size inputs, not color inputs
            const swatchInputs = this.querySelectorAll('[data-swatch-input]');
            
            // Disable each input, except for color inputs
            swatchInputs.forEach(input => {
                // Only disable size inputs during the transition, allow colors to be clicked
                if (input.name !== 'COLOR') {
                    input.disabled = true;
                    input.classList.add('disabled');
                }
            });
            
            // Add a visual indicator that inputs are disabled
            this.classList.add('swatch-inputs-disabled');
        }

        /**
         * Enable all swatch inputs in the quick view that aren't sold out
         * 
         * @returns {undefined}
         */
        enableSwatchInputs() {
            if (!this._swatchInputsDisabled) return;
            
            this._swatchInputsDisabled = false;
            
            // Find all swatch inputs in the quick view
            const swatchInputs = this.querySelectorAll('[data-swatch-input]');
            const variantSelector = this.querySelector('variant-selector select');
            
            if (!variantSelector) return;
            
            // Enable each input based on type and availability
            swatchInputs.forEach(input => {
                const optionValue = input.value;
                const optionName = input.name;
                
                if (optionName === 'COLOR') {
                    // Always enable color inputs - they should always be clickable
                    input.disabled = false;
                    input.classList.remove('disabled');
                } else {
                    // For sizes and other options, check if this option value is available in any enabled variant
                    const hasAvailableVariant = Array.from(variantSelector.options).some(option => {
                        return option.hasAttribute('data-enabled') && 
                               option.textContent.includes(optionValue);
                    });
                    
                    if (hasAvailableVariant) {
                        input.disabled = false;
                        input.classList.remove('disabled');
                    } else {
                        // Keep sold out sizes disabled
                        input.disabled = true;
                        input.classList.add('disabled');
                    }
                }
            });
            
            // Remove the temporary disable state
            this.classList.remove('swatch-inputs-disabled');
        }

        /**
         * On swatch change
         * 
         * @param {object} product Product data.
         * @param {object} variant Variant data.
         * @returns {undefined}
         */
        onSwatchChange(product, variant) {
            console.log("[QuickView] Variant changed to:", variant ? `${variant.id} (${variant.title})` : 'undefined');
            
            // Check if product and variant are valid
            if (!product || !variant) {
                console.error('[QuickView] Invalid product or variant data in onSwatchChange');
                return;
            }
            
            // Verify this is for the current product
            if (product.id !== this.currentProductId) {
                console.log(`[QuickView] Ignoring swatch change for non-current product (received: ${product.id}, current: ${this.currentProductId})`);
                return;
            }
            
            // Disable all swatch inputs temporarily (except colors)
            console.log('[QuickView] Temporarily disabling swatch inputs');
            this.disableSwatchInputs();
            
            // Immediately update the button state and UI elements that don't need inventory data
            console.log('[QuickView] Performing immediate UI update');
            this._immediateUIUpdate(product, variant);
            
            // If there's a pending variant change, clear it
            if (this._debounceTimer) {
                console.log('[QuickView] Clearing existing debounce timer');
                clearTimeout(this._debounceTimer);
            }
            
            // Store the current variant change request
            this._pendingVariantChange = { product, variant };
            console.log('[QuickView] Stored pending variant change, setting debounce timer');
            
            // Set a debounce timer to process the change after a delay
            this._debounceTimer = setTimeout(() => {
                // Check if this is still the current product
                if (product.id !== this.currentProductId) {
                    console.log(`[QuickView] Skipping debounced variant change for inactive product ${product.id}`);
                    return;
                }
                
                console.log('[QuickView] Debounce timer expired, processing variant change');
                // Process the most recent variant change
                this._processVariantChange(this._pendingVariantChange.product, this._pendingVariantChange.variant);
                // Clear the pending change
                this._pendingVariantChange = null;
                
                // Re-enable available swatch inputs after processing
                setTimeout(() => {
                    console.log('[QuickView] Re-enabling available swatch inputs');
                    this.enableSwatchInputs();
                    
                    // Update disabled states based on current selection
                    console.log('[QuickView] Updating swatch availability');
                    this.updateSwatchAvailability(product, variant);
                }, 500);
            }, 300);
        }

        /**
         * Update swatch availability based on current variant selection
         * 
         * @param {object} product Product data
         * @param {object} currentVariant Current variant
         * @returns {undefined}
         */
        updateSwatchAvailability(product, currentVariant) {
            if (!product || !currentVariant) return;
            
            const swatchInputs = this.querySelectorAll('[data-swatch-input]');
            const variantSelector = this.querySelector('variant-selector select');
            
            if (!variantSelector) return;
            
            // Get current selections
            const currentSelections = {};
            swatchInputs.forEach(input => {
                if (input.checked) {
                    currentSelections[input.name] = input.value;
                }
            });
            
            // Check each swatch input
            swatchInputs.forEach(input => {
                const optionName = input.name;
                const optionValue = input.value;
                
                // Always enable color options, we'll handle ATC button state separately
                if (optionName === 'COLOR') {
                    input.disabled = false;
                    input.classList.remove('disabled');
                    return;
                }
                
                // For other options (like size), check availability
                // Create test selection with this option
                const testSelections = { ...currentSelections, [optionName]: optionValue };
                
                // Check if this combination exists in an available variant
                const hasAvailableVariant = product.variants.some(variant => {
                    if (!variant.available) return false;
                    
                    // Check if all selected options match this variant
                    return Object.entries(testSelections).every(([name, value]) => {
                        const optionIndex = product.options.findIndex(opt => opt.name === name);
                        return optionIndex === -1 || variant.options[optionIndex] === value;
                    });
                });
                
                // Update input state for non-color inputs
                if (hasAvailableVariant) {
                    input.disabled = false;
                    input.classList.remove('disabled');
                } else {
                    input.disabled = true;
                    input.classList.add('disabled');
                }
            });
        }

        /**
         * Process a variant change after debounce
         * 
         * @param {object} product Product data.
         * @param {object} variant Variant data.
         * @returns {undefined}
         */
        _processVariantChange(product, variant) {
            if (!product || !variant) {
                console.error('[QuickView] Invalid product or variant data in _processVariantChange');
                return;
            }
            if (product.id !== this.currentProductId) {
                console.log(`[QuickView] Skipping variant processing for non-current product (received: ${product.id}, current: ${this.currentProductId})`);
                return;
            }
            console.log(`[QuickView] Processing variant change to ${variant.title} (${variant.id})`);
            const variantOption1 = variant.option1;
            const variantOption2 = variant.option2;
            const colorOption = variant.option2 || variant.option1;
            console.log(`[QuickView] Looking for images matching color: ${colorOption}`);
            const variantImages = [];
            if (product.media && product.media.length > 0) {
                const altRegex = new RegExp(`${colorOption}|${colorOption.replace(' ', '-')}|${colorOption.replace(' ', '')}`, 'i');
                console.log(`[QuickView] Searching for images with alt text matching: ${altRegex}`);
                product.media.forEach((item, index) => {
                    const alt = item.alt ? item.alt : '';
                    console.log(`[QuickView] Checking image ${index}: "${alt}"`);
                    if (alt.match(altRegex)) {
                        console.log(`[QuickView] Match found for image ${index}`);
                        variantImages.push(index);
                    }
                });
                console.log(`[QuickView] Found ${variantImages.length} matching images for variant color`);
            } else {
                console.log('[QuickView] No product media found');
            }
            try {
                console.log('[QuickView] Checking inventory status');
                const inventoryData = this.productInventoryQuantity || window.productsInventoryQuantity;
                if (inventoryData && inventoryData.length > 0) {
                    console.log(`[QuickView] Inventory data available, items: ${inventoryData.length}`);
                    this.updateProductInventoryQuantity(inventoryData, variant);
                } else {
                    console.log('[QuickView] No inventory data available');
                }
            } catch (error) {
                console.error("[QuickView] Error during variant change inventory check:", error);
            }
            // Robust: If Swiper is not yet initialized, queue the image sync for after init
            if (!this.slider) {
                console.log("[QuickView] Slider not available, attempting to reinitialize and queue image sync");
                this._queuedSliderSync = () => this._updateSliderImages(variantImages, colorOption);
                this.initSlider();
                return;
            }
            this._updateSliderImages(variantImages, colorOption);
        }

        /**
         * Helper method to update slider images
         * 
         * @param {Array} variantImages Array of indices of variant-specific images
         * @param {string} colorOption The color option to match against slide data
         * @returns {undefined}
         */
        _updateSliderImages(variantImages, colorOption) {
            // Get current slides in the DOM
            const slides = this.querySelectorAll('.swiper-slide');
            console.log(`[QuickView] Found ${slides.length} slides in the carousel`);
            
            // If we found variant-specific images, switch to the first one
            if (variantImages.length > 0 && this.slider) {
                console.log(`[QuickView] Sliding to variant-specific image at index ${variantImages[0]}`);
                try {
                    this.slider.slideTo(variantImages[0], 500);
                    console.log('[QuickView] Slide transition initiated');
                } catch (error) {
                    console.error('[QuickView] Error during slide transition:', error);
                }
                return;
            } 
            
            // If we don't have variant-specific images from the media array, search in DOM
            console.log("[QuickView] No direct matches in media array, searching in DOM");
            
            // Check variant color (normalize to handle different formats)
            const normalizedColorOption = colorOption
                ? colorOption.replace(/\s+/g, '').toLowerCase()
                : '';
                
            console.log(`[QuickView] Normalized color for search: "${normalizedColorOption}"`);
            
            // Look for slides with data-media-item attribute that contains the variant color
            let foundIndex = -1;
            
            slides.forEach((slide, index) => {
                const mediaItem = slide.getAttribute('data-media-item');
                if (mediaItem) {
                    // Normalize both strings for comparison
                    const normalizedMediaItem = mediaItem.replace(/\s+/g, '').toLowerCase();
                    console.log(`[QuickView] Checking slide ${index} with data: "${normalizedMediaItem}"`);
                    
                    // Check if the media item contains the variant option (normalized comparison)
                    if (normalizedMediaItem.includes(normalizedColorOption)) {
                        console.log(`[QuickView] Match found for slide ${index}`);
                        if (foundIndex === -1) foundIndex = index;
                    }
                } else {
                    console.log(`[QuickView] Slide ${index} has no data-media-item attribute`);
                }
            });
            
            if (foundIndex >= 0) {
                console.log(`[QuickView] Sliding to matched slide at index ${foundIndex}`);
                try {
                    this.slider.slideTo(foundIndex, 500);
                } catch (error) {
                    console.error('[QuickView] Error during slide transition:', error);
                }
            } else {
                console.log("[QuickView] No direct matches found, trying fallback search");
                
                // Fallback: try to match color name parts
                const colorParts = colorOption ? colorOption.split(/[\s-]+/).filter(part => part.length > 2) : [];
                console.log(`[QuickView] Color parts for fallback search:`, colorParts);
                
                let bestMatchIndex = -1;
                let bestMatchScore = 0;
                
                slides.forEach((slide, index) => {
                    const mediaItem = slide.getAttribute('data-media-item');
                    if (mediaItem) {
                        const normalizedMediaItem = mediaItem.toLowerCase();
                        let matchScore = 0;
                        
                        colorParts.forEach(part => {
                            if (normalizedMediaItem.includes(part.toLowerCase())) {
                                matchScore++;
                            }
                        });
                        
                        console.log(`[QuickView] Slide ${index} score: ${matchScore}`);
                        
                        if (matchScore > bestMatchScore) {
                            bestMatchScore = matchScore;
                            bestMatchIndex = index;
                        }
                    }
                });
                
                if (bestMatchIndex >= 0) {
                    console.log(`[QuickView] Sliding to best match at index ${bestMatchIndex} (score: ${bestMatchScore})`);
                    try {
                        this.slider.slideTo(bestMatchIndex, 500);
                    } catch (error) {
                        console.error('[QuickView] Error during slide transition:', error);
                    }
                } else {
                    // If still no match, go back to the first image
                    console.log("[QuickView] No matches found, returning to first slide");
                    try {
                        this.slider.slideTo(0, 500);
                    } catch (error) {
                        console.error('[QuickView] Error during slide transition:', error);
                    }
                }
            }
        }

        /**
         * Update product inventory quantity display
         * 
         * @param {Array} inventoryQuantity Inventory data.
         * @param {object} variant Current variant data.
         * @returns {undefined}
         */
        async updateProductInventoryQuantity(inventoryQuantity, variant) {
            if (!variant) {
                console.log('[QuickView] No variant provided for inventory update');
                return;
            }
            
            try {
                console.log(`[QuickView] Updating inventory UI for variant ${variant.id} (${variant.title})`);
                
                // Get fresh cart data
                console.log('[QuickView] Fetching latest cart data');
                const req = await fetch(window.Shopify.routes.root + 'cart.js');
                const res = await req.json();
                console.log('[QuickView] Cart data received, items:', res.items.length);
                
                const variantInCart = res.items.find(item => item.variant_id === variant.id);
                if (variantInCart) {
                    console.log(`[QuickView] Variant ${variant.id} found in cart, quantity: ${variantInCart.quantity}`);
                } else {
                    console.log(`[QuickView] Variant ${variant.id} not in cart`);
                }
                
                const addToCartBtn = this.querySelector(".product-addtocart__button");
                const quantityItem = this.querySelector(".js-in-stock");
                const inStockAllAddedEl = this.querySelector(".js-in-stock-all-added");
                
                if (!addToCartBtn) {
                    console.error('[QuickView] Add to cart button not found');
                    return;
                }
                
                const productAssociation = this.querySelector('product-association');
                let isSizeSelected = false;
                
                if (productAssociation) {
                    const allOptions = productAssociation.querySelectorAll('[data-option]');
                    for (let option of allOptions) {
                        const optionName = option.getAttribute('data-option');
                        if (optionName && optionName.toLowerCase().includes('size')) {
                            const sizeInput = option.querySelector('input[data-swatch-input]:checked');
                            isSizeSelected = sizeInput !== null;
                            break;
                        }
                    }
                }
                
                // Reset UI first
                console.log('[QuickView] Resetting inventory UI elements');
                if (quantityItem) quantityItem.classList.add("hidden");
                if (inStockAllAddedEl) inStockAllAddedEl.classList.add("hidden");
                
                if (!isSizeSelected) {
                    addToCartBtn.disabled = true;
                    addToCartBtn.setAttribute('disabled', 'disabled');
                    addToCartBtn.classList.add("disabled");
                    return;
                }
                
                // First check if the variant is available at all
                if (!variant.available) {
                    // Disable the button for sold-out variants
                    console.log(`[QuickView] Variant ${variant.id} is not available, disabling button`);
                    addToCartBtn.disabled = true;
                    addToCartBtn.setAttribute('disabled', 'disabled');
                    addToCartBtn.classList.add("disabled");
                    return;
                }
                
                // Enable the button by default for available variants
                console.log(`[QuickView] Variant ${variant.id} is available, enabling button`);
                addToCartBtn.removeAttribute('disabled');
                addToCartBtn.classList.remove("disabled");
                
                // Find the inventory data for this variant - check current instance inventory first
                let variantInventory = 0;
                const variantIdStr = variant.id.toString();
                
                // Make sure we use the latest inventory data
                const latestInventoryData = this.productInventoryQuantity || inventoryQuantity || window.productsInventoryQuantity;
                console.log('[QuickView] Inventory data sources:');
                console.log('  - this.productInventoryQuantity:', this.productInventoryQuantity ? this.productInventoryQuantity.length + ' items' : 'null');
                console.log('  - inventoryQuantity parameter:', inventoryQuantity ? inventoryQuantity.length + ' items' : 'null');
                console.log('  - window.productsInventoryQuantity:', window.productsInventoryQuantity ? window.productsInventoryQuantity.length + ' items' : 'null');
                
                if (!latestInventoryData || latestInventoryData.length === 0) {
                    console.log("[QuickView] No inventory data available, using defaults");
                    // Use a default value for available variants
                    variantInventory = variant.available ? 10 : 0;
                    console.log(`[QuickView] Using default inventory: ${variantInventory}`);
                } else {
                    // First log the inventory data for debugging
                    console.log(`[QuickView] Looking for variant ${variantIdStr} in inventory data:`, latestInventoryData);
                    
                    // Find direct match in the inventory data array
                    const inventoryEntry = latestInventoryData.find(item => {
                        if (!item) return false;
                        const parts = item.split(':');
                        return parts.length === 2 && parts[0] === variantIdStr;
                    });
                    
                    if (inventoryEntry) {
                        const [id, qty] = inventoryEntry.split(':');
                        variantInventory = parseInt(qty);
                        console.log(`[QuickView] Found exact inventory match for variant ${variant.id}: ${variantInventory} items`);
                    } else {
                        console.log(`[QuickView] No inventory data found for variant ${variant.id}, assuming default stock`);
                        // Assume a default stock level for available variants
                        variantInventory = variant.available ? 10 : 0;
                    }
                }

                const variantInCartQty = variantInCart ? variantInCart.quantity : 0;
                console.log(`[QuickView] Variant ${variant.id}: ${variantInventory} in stock, ${variantInCartQty} in cart`);
                
                // Check if all inventory is in cart
                if (variantInventory > 0 && variantInCartQty >= variantInventory) {
                    // Use both techniques to force disable the button
                    console.log(`[QuickView] All items (${variantInventory}) for variant ${variant.id} are in cart (${variantInCartQty}), disabling button`);
                    addToCartBtn.disabled = true;
                    addToCartBtn.setAttribute('disabled', 'disabled');
                    addToCartBtn.classList.add("disabled");
                    
                    if (inStockAllAddedEl) {
                        console.log('[QuickView] Showing "all items in cart" message');
                        inStockAllAddedEl.classList.remove("hidden");
                    }
                } else if (variantInventory > 0 && variantInventory <= 10) {
                    // Show low stock warning
                    const remainingStock = variantInventory - variantInCartQty;
                    
                    if (remainingStock > 0 && quantityItem) {
                        console.log(`[QuickView] Low stock: ${remainingStock} items left, showing warning`);
                        const inStockTitle = quantityItem.querySelector(".js-in-stock-title");
                        const inStockQuantity = quantityItem.querySelector(".js-in-stock-quantity");

                        if (inStockTitle && inStockQuantity) {
                            inStockTitle.innerHTML = variant.option1;
                            inStockQuantity.innerHTML = remainingStock;
                            quantityItem.classList.remove("hidden");
                            console.log(`[QuickView] Showing inventory message: ${variant.option1}: Hurry! Only ${remainingStock} left in stock`);
                        } else {
                            console.log('[QuickView] Missing stock title or quantity elements');
                        }
                    } else {
                        console.log(`[QuickView] No remaining stock or missing UI elements`);
                    }
                } else {
                    console.log(`[QuickView] Variant ${variant.id} has ${variantInventory} items in stock, no low stock warning needed`);
                }
            } catch (error) {
                console.error("[QuickView] Error updating product inventory quantity:", error);
            }
        }

        updateLineProperty(variant) {
            const propertyComparePrice = document.querySelector('[name="properties[_compareAtPrice]"]');
            const propertyLPROP = document.querySelector('[name="properties[_LPROP]"]');
            
            if (!variant || !propertyComparePrice || !propertyLPROP) return;
            
            const difference = +variant.compare_at_price - variant.price;
            const percentDifference = Math.floor((difference * 100) / variant.compare_at_price);

            if (
                variant.compare_at_price !== null
                && variant.compare_at_price > variant.price
                && percentDifference >= 40
            ) {
                propertyLPROP.disabled = false;
            } else {
                propertyLPROP.disabled = true;
            }

            if (
                variant.compare_at_price !== null
                && variant.compare_at_price > variant.price
            ) {
                propertyComparePrice.disabled = false;
            } else {
                propertyComparePrice.disabled = true;
            }
        }

        /**
         * Close quick view
         * 
         * @returns {undefined}
         */
        close() {
            console.log('[QuickView] Closing quick view');
            
            // Capture the current product ID for cleanup
            const closingProductId = this.currentProductId;
            console.log(`[QuickView] Closing product ID: ${closingProductId}`);
            
            this.classList.remove('loaded');
            this.classList.add('hidden');

            this.disableFocusTrap();

            // Clean up event listeners first before DOM elements are removed
            if (this._variantChangeListenerAdded && this._variantChangeHandler) {
                console.log('[QuickView] Removing variant-change event listener');
                window.removeEventListener('variant-change', this._variantChangeHandler);
                this._variantChangeListenerAdded = false;
            }
            
            if (this._cartUpdateListenerAdded && this._cartUpdateHandler) {
                console.log('[QuickView] Removing cart:updated event listener');
                document.removeEventListener('cart:updated', this._cartUpdateHandler);
                this._cartUpdateListenerAdded = false;
            }
            
            // Clean up any form submit handlers
            const form = this.querySelector('[data-product-form]');
            if (form && form._quickviewSubmitHandler) {
                console.log('[QuickView] Removing form submit handler');
                form.removeEventListener('submit', form._quickviewSubmitHandler);
                form._quickviewSubmitHandler = null;
            }

            setTimeout(() => {
                console.log('[QuickView] Destroying slider and removing content');
                this.destroySlider();

                let content = document.querySelector('.quickview__content');

                if (content) {
                    content.remove();
                    this.setAttribute('aria-hidden', 'true');
                }

                document.querySelector('body').classList.remove('quickview-active');
                
                document.querySelector('.quickview__overlay').classList.add('hidden');
                console.log('[QuickView] Quick view fully closed');
                
                // Clean up component state
                this.slider = null;
                this.productInventoryQuantity = null;
                window.productsInventoryQuantity = null;
                this.activeVariant = null;
                this.currentProductId = null;
                
                // Clear any pending timers
                if (this._debounceTimer) {
                    clearTimeout(this._debounceTimer);
                    this._debounceTimer = null;
                }
                this._pendingVariantChange = null;
                
                // Reset event handlers
                this._variantChangeHandler = null;
                this._cartUpdateHandler = null;
                
                console.log('[QuickView] Component state reset');
            }, 350);
        }

        /**
         * Load quick view content
         * 
         * @param {object} product Product data.
         * @returns {string} HTML code
         */
        loadContent(product) {
            let content = quickViewTemplate.quick_view_content_layout;

            let productImages = '';

            const mediaItems = product.media || product.images.map(img => ({ media_type: 'image', src: img.src, alt: img.alt }));

            mediaItems.forEach(media => {
                const altText = media.alt || '';
                let slideInner = '';

                if (media.media_type === 'video') {
                    const sources = (media.sources || [])
                        .map(s => `<source src="${s.url}" type="${s.mime_type}">`)
                        .join('');
                    slideInner = `<video autoplay loop muted playsinline class="quickview__carousel-video">${sources}</video>`;
                } else if (media.media_type === 'external_video') {
                    let embedUrl = media.embed_url;
                    if (!embedUrl) {
                        embedUrl = media.host === 'youtube'
                            ? `https://www.youtube.com/embed/${media.external_id}?autoplay=1&mute=1&loop=1&playlist=${media.external_id}`
                            : `https://player.vimeo.com/video/${media.external_id}?autoplay=1&muted=1&loop=1`;
                    }
                    slideInner = `<iframe src="${embedUrl}" class="quickview__carousel-video" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>`;
                } else {
                    const src = media.src || (media.image && media.image.src) || '';
                    slideInner = `<img src="${src}" alt="${altText}" loading="lazy"/>`;
                }

                productImages += `<div class="swiper-slide quickview__carousel-item" data-media-item="${altText}">${slideInner}</div>`;
            });

            if (mediaItems.length > 1) {
                this.enable_carousel = true;
            }

            content = content.replace(/{{productImage}}/g, productImages);
            
            content = content.replace(/{{productTitle}}/g, product.title);

            content = content.replace(/{{productVendor}}/g, product.vendor);


            let product_price = `<span data-rki-discount-price class="rki-discount-price text-base font-secondary mb-2 inline-block"></span>
            <span class="quickview__price-current ${ product.variants[0].compare_at_price > 0 ? 'text-error' : '' }" data-price data-rki-original-price data-dtp-original-price>${product.variants[0].price}</span>`;

            if (product.variants[0].compare_at_price > 0) {
              const difference = product.variants[0].compare_at_price - product.price_min;
              const percentDifference = Math.floor((difference * 100) / product.compare_at_price);

              product_price += `<span class="${percentDifference >= 40 ? 'quickview__price-compared ' : ''}font-primary text-base text-disabled-dark ml-2 inline-block line-through" data-compareatprice>${product.variants[0].compare_at_price}</span>`;
              content = content.replace(/{{componentAtPrice}}/g, product.variants[0].compare_at_price);
            } else {
              content = content.replace(/{{componentAtPrice}}/g, `${(product.price_max > 0 && product.price_max > product.price) ? product.price_max : "0" }`);
            }

            content = content.replace(/{{productPrice}}/g, product_price);

            let pure_price = product.variants[0].price / 100;

            content = content.replace(/{{productPurePrice}}/g, pure_price);

            content = content.replace(/{{productReKeepIt}}/g, this.initReKeepIt(product));
            
            content = content.replace(/{{productId}}/g, product.id);
            
            // Check if the default variant is available (first variant)
            const isVariantAvailable = product.variants[0].available;
            
            // Add disabled attribute to the Add to Cart button if variant is sold out
            const disabledAttr = !isVariantAvailable ? 'disabled="disabled"' : '';
            const disabledClass = !isVariantAvailable ? 'disabled' : '';
            
            // Replace the content with the appropriate button state
            content = content.replace(/{{addToCartDisabled}}/g, disabledAttr);
            content = content.replace(/{{addToCartClass}}/g, disabledClass);
            
            content = content.replace(/{{variant}}/g, product.variants[0].id);

            content = content.replaceAll(/{{productHandle}}/g, product.handle);

            content = content.replaceAll(/{{currentVariantSku}}/g, product.variants[0].sku);

            content = content.replaceAll(/{{currentVariant.option2}}/g, product.variants[0].option2);
            
            return content;
        }

        /**
         * Init ReKeepIt Discount
         * 
         * @param {object} product product.
         * @returns {string} HTML code
         */
        initReKeepIt(product) {
            let rki_product = product;
            let rki_variant_id = rki_product.variants[0].id;
            let rki_widget_id = product.id;
            let rki_discount;

            // Detect if this product has an RKI discount
            let has_rki_discount = false;

            rki_product.tags.forEach((tag) => {
                let tag_downcase = tag.toLowerCase();

                if(tag_downcase.includes('rki:')) {
                    has_rki_discount = true;
                    rki_discount = tag.split(":");
                    rki_discount = rki_discount[1];
                    rki_discount = rki_discount * 1.00000000;
                }
            });

            // If RKI Discount - Assemble discount JSON
            let rki_discount_multiplier = (100 - rki_discount) / 100.00000000;
            let variants = [];

            rki_product.variants.forEach((variant) => {
                let rki_discount_price = variant.price * rki_discount_multiplier;

                variants.push({
                    "id": variant.id,
                    "original_price": {
                        "raw": variant.price,
                        "formatted": window.theme.money.format(variant.price)
                    },
                    "discount_price": {
                        "raw": rki_discount_price,
                        "formatted": window.theme.money.format(rki_discount_price),
                    },
                    "discount": {
                        "raw": (variant.price - rki_discount_price),
                        "formatted": window.theme.money.format((variant.price - rki_discount_price))
                    }
                });
            })

            if(has_rki_discount) {
                let rki_discount_json = {
                    "discount": rki_discount,
                    "variants": variants
                };

                // Render Opt-In Widget
                let html = `<div
                    class="rki"
                    data-rki-widget='product'
                    data-rki-id='${rki_widget_id}'
                    data-rki-variant-id='${rki_variant_id}'
                    data-rki-discount-json='${JSON.stringify(rki_discount_json)}'>
                </div>`;

                return html;
            }

            return "";
        }

        /**
         * Init quick view focus trap
         * 
         * @returns {undefined}
         */
        initFocusTrap() {
            this.focusTrap = focusTrap.createFocusTrap(this, {});
        }

        /**
         * Disable quick view focus trap
         *  
         * @returns {undefined}
         */
        disableFocusTrap() {
            if (this.focusTrap) {
                this.focusTrap.deactivate();
            }
        }

        /**
         * Ative quick view focus trap
         * 
         * @returns {undefined}
         */
        activeFocusTrap() {
            if (this.focusTrap) {
                this.focusTrap.activate();
            }
        }

        /**
         * Init quantity selector
         * 
         * @return {undefined}
         */
        initQtySelector() {
            let QtySelector = this.querySelectorAll('[data-quantity-selector]');
    
            QtySelector.forEach(element => {
                element.querySelector('[data-quantity-selector-minus]').addEventListener('click', event => {
                    event.preventDefault();
    
                    let input = event.target.parentElement.querySelector('input');
    
                    this.decreaseQtySelector(input);
                });
    
                element.querySelector('[data-quantity-selector-plus]').addEventListener('click', event => {
                    event.preventDefault();
    
                    let input = event.target.parentElement.querySelector('input');
    
                    this.increaseQtySelector(input);
                });
            });
        }

        /**
         * Decrease input value
         * 
         * @param {input} input A input for the quantity selector.
         * @return {undefined}
         */
        decreaseQtySelector(input) {
            let value = parseInt(input.value);

            if (value > 0) {
                value--;
                input.value = value;

                let event = new CustomEvent('change', {'detail': input });
                input.dispatchEvent(event);
            }
        }

        /**
         * Increase input value
         * 
         * @param {input} input A input for the quantity selector.
         * @return {undefined}
         */
         increaseQtySelector(input) {
            let value = parseInt(input.value);
            value++;
            input.value = value;

            let event = new CustomEvent('change', {'detail': input });
            input.dispatchEvent(event);
        }

        /**
         * Fetch inventory data for a product
         * 
         * @param {object} product Product data
         * @returns {Promise} Promise with inventory data
         */
        async fetchProductInventory(product) {
            console.log(`[QuickView] Fetching inventory for product ${product.id} (${product.title})`);
            try {
                // First try to fetch from our dedicated inventory JSON endpoint
                console.log(`[QuickView] Requesting inventory JSON from /products/${product.handle}?view=inventory-json`);
                const response = await fetch(`/products/${product.handle}?view=inventory-json`);
                const text = await response.text();
                console.log('[QuickView] Received response, text length:', text.length);
                
                // Clean the response text (remove any HTML that might be wrapping the JSON)
                const cleanedText = text.trim().replace(/^[\s\S]*?(\[[\s\S]*?\])[\s\S]*?$/, '$1');
                console.log('[QuickView] Cleaned text length:', cleanedText.length);
                
                try {
                    // Try to parse the JSON response
                    const inventoryData = JSON.parse(cleanedText);
                    console.log("[QuickView] Fetched inventory data from JSON template:", inventoryData);
                    
                    // Ensure we have an array of strings in the format "variant_id:quantity"
                    if (Array.isArray(inventoryData) && inventoryData.length > 0) {
                        // Store the complete inventory data for this product
                        this.productInventoryQuantity = inventoryData;
                        // Allow global access too for other components
                        window.productsInventoryQuantity = inventoryData;
                        
                        // Log all variant IDs from the inventory data for debugging
                        console.log("[QuickView] Available variants in inventory data:", 
                            inventoryData.map(item => {
                                const [id, qty] = item.split(':');
                                return `${id} (qty: ${qty})`;
                            }).join(', ')
                        );
                        
                        return inventoryData;
                    } else {
                        console.log("[QuickView] Invalid inventory data format, expected array:", inventoryData);
                    }
                } catch (jsonError) {
                    console.error("[QuickView] Error parsing inventory JSON:", jsonError, "Raw text:", cleanedText);
                    
                    // Fallback: Look for inventory data in the product page HTML
                    console.log('[QuickView] Trying fallback: fetching product page HTML');
                    const pageResponse = await fetch(`/products/${product.handle}`);
                    const html = await pageResponse.text();
                    console.log('[QuickView] Product page HTML received, length:', html.length);
                    
                    // Look for the inventory script in the HTML
                    const inventoryRegex = /window\.productsInventoryQuantity\s*=\s*(\[.*?\]);/s;
                    const match = html.match(inventoryRegex);
                    
                    if (match && match[1]) {
                        console.log('[QuickView] Found inventory script in HTML');
                        // Parse the inventory data
                        try {
                            const inventoryData = JSON.parse(match[1]);
                            console.log("[QuickView] Found inventory data in page HTML:", inventoryData);
                            // Store the data
                            this.productInventoryQuantity = inventoryData;
                            window.productsInventoryQuantity = inventoryData;
                            return inventoryData;
                        } catch (err) {
                            console.error("[QuickView] Error parsing inventory data from HTML:", err);
                        }
                    } else {
                        console.log('[QuickView] No inventory script found in HTML');
                    }
                }
                
                // Last resort: Create inventory data based on variants
                console.log("[QuickView] Generating inventory data from variant information");
                const inventoryData = await this.fetchInventoryLevels(product);
                // Store the generated data
                this.productInventoryQuantity = inventoryData;
                window.productsInventoryQuantity = inventoryData;
                return inventoryData;
            } catch (error) {
                console.error("[QuickView] Error fetching product inventory:", error);
                console.log('[QuickView] Using fallback inventory method');
                const inventoryData = await this.fetchInventoryLevels(product);
                // Store the fallback data
                this.productInventoryQuantity = inventoryData;
                window.productsInventoryQuantity = inventoryData;
                return inventoryData;
            }
        }
        
        /**
         * Fetch inventory levels for product variants
         * 
         * @param {object} product Product data
         * @returns {Array} Inventory data array
         */
        async fetchInventoryLevels(product) {
            try {
                // Get cart data to check if any variants are already in cart
                const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
                const cartData = await cartResponse.json();
                
                // Create inventory entries for each variant
                const inventoryData = product.variants.map(variant => {
                    let quantity = 0;
                    
                    if (variant.available) {
                        // For available variants, default to a safe value (10)
                        quantity = 10;
                        
                        // Check if this variant is in the cart
                        const variantInCart = cartData.items.find(item => item.variant_id === variant.id);
                        if (variantInCart && variantInCart.quantity >= quantity) {
                            // If all estimated inventory is in cart, set to exact cart quantity
                            // This will trigger the "all items in cart" message
                            quantity = variantInCart.quantity;
                        }
                    } else {
                        // For sold out variants, set to 0
                        quantity = 0;
                    }
                    
                    // Format as 'variant_id:quantity'
                    return `${variant.id}:${quantity}`;
                });
                
                return inventoryData;
            } catch (error) {
                console.error("Error fetching inventory levels:", error);
                // Return simple inventory data as fallback
                return product.variants.map(variant => {
                    return `${variant.id}:${variant.available ? 10 : 0}`;
                });
            }
        }
        
        /**
         * Continue quick view initialization after inventory data is ready
         * 
         * @param {object} product Product data
         * @param {string} [variantId] Optional variant id to preselect
         * @returns {undefined}
         */
        continueQuickViewInit(product, variantId) {
            console.log(`[QuickView] Continuing initialization for product ${product.id} (${product.title})`);
            // Store reference to the product ID we're initializing
            const initializingProductId = product.id;
            setTimeout(() => {
                // Only proceed if this is still the current product
                if (this.currentProductId !== initializingProductId) {
                    console.log(`[QuickView] Skipping delayed initialization for ${initializingProductId}, current product is now ${this.currentProductId}`);
                    return;
                }
                console.log('[QuickView] Delayed initialization running');
                this.classList.remove('hidden');
                this.setAttribute('aria-hidden', 'false');
                this.activeFocusTrap();
                this.initQtySelector();
                if(document.querySelector('[data-quickview-container]').querySelector('[data-quickview-rekeepit]')) {
                    console.log('[QuickView] Refreshing ReKeepIt widget');
                    rekeepit.refresh();
                }
                // Initialize inventory check for the first variant (selected or first available or default)
                console.log('[QuickView] Finding initial variant');
                let variant;
                if (variantId) {
                    // Use the passed variantId if provided
                    console.log(`[QuickView] Using passed variantId: ${variantId}`);
                    variant = product.variants.find(v => v.id == variantId);
                } else {
                    const variantIdParam = new URLSearchParams(window.location.search).get('variant');
                    if (variantIdParam) {
                        // Try to match the URL variant parameter
                        console.log(`[QuickView] URL contains variant parameter: ${variantIdParam}`);
                        variant = product.variants.find(v => v.id == variantIdParam);
                    }
                }
                if (!variant) {
                    console.log('[QuickView] No variant found from param or passed id, selecting first available or first variant');
                    // Fallback to first available or first variant
                    variant = product.variants.find(v => v.available) || product.variants[0];
                }
                // Set initializing variant flag
                this._initializingVariantId = variant.id;
                console.log(`[QuickView] Selected initial variant: ${variant.id} (${variant.title}), available: ${variant.available}`);

                const productAssociation = this.querySelector('product-association');
                if (productAssociation && typeof productAssociation.selectColorOnly === 'function') {
                    const selectSwatchColor = () => {
                        productAssociation.selectColorOnly(variant);
                        this._processVariantChange(product, variant);
                        productAssociation.removeEventListener('swatches-ready', selectSwatchColor);
                    };
                    // If swatches are already rendered, call immediately (heuristic: check for at least one swatch input)
                    if (productAssociation.querySelector('[data-swatch-input]')) {
                        productAssociation.selectColorOnly(variant);
                        this._processVariantChange(product, variant);
                    } else {
                        productAssociation.addEventListener('swatches-ready', selectSwatchColor);
                    }
                }

                // Initial UI update
                this._immediateUIUpdate(product, variant);
                // Also check inventory quantities
                const inventoryData = this.productInventoryQuantity || window.productsInventoryQuantity;
                if (inventoryData && inventoryData.length > 0) {
                    console.log(`[QuickView] Using inventory data for initial check: ${inventoryData.length} items`);
                    this.updateProductInventoryQuantity(inventoryData, variant);
                } else {
                    console.log(`[QuickView] No inventory data available for initial variant: ${variant.id}`);
                }
            }, 500);

            // Create named handlers that reference the current product ID to filter events properly
            this._variantChangeHandler = (event) => {
                // Only skip the very first event if it matches the initializing variant
                if (this._initializingVariantId) {
                    if (event.detail.variant.id === this._initializingVariantId) {
                        // This is the initial variant, skip processing, clear the flag
                        this._initializingVariantId = null;
                        return;
                    } else {
                        // This is a real user change, clear the flag and process
                        this._initializingVariantId = null;
                    }
                }
                if (event.detail.product.id == this.currentProductId) {
                    this.onSwatchChange(event.detail.product, event.detail.variant);
                }
            };

            this._cartUpdateHandler = () => {
                console.log('[QuickView] cart:updated event received, refreshing inventory UI');
                // Only process cart updates for the current product ID
                if (this.currentProductId === product.id) {
                    const variant = this.getSelectedVariant(product);
                    const inventoryData = this.productInventoryQuantity || window.productsInventoryQuantity;
                    if (variant && inventoryData && inventoryData.length > 0) {
                        console.log(`[QuickView] Updating inventory for variant ${variant.id} after cart update`);
                        this.updateProductInventoryQuantity(inventoryData, variant);
                    } else {
                        console.log('[QuickView] Missing variant or inventory data for cart update');
                    }
                } else {
                    console.log(`[QuickView] Ignoring cart update for inactive product (current: ${this.currentProductId}, was: ${product.id})`);
                }
            };

            // Add event listener for variant changes
            if (!this._variantChangeListenerAdded) {
                console.log('[QuickView] Adding variant-change event listener');
                this._variantChangeListenerAdded = true;
                window.addEventListener('variant-change', this._variantChangeHandler);
            } else {
                console.log('[QuickView] Updating existing variant-change handler for new product');
                // Ensure handler is updated even if listener is already added
                window.removeEventListener('variant-change', this._variantChangeHandler);
                window.addEventListener('variant-change', this._variantChangeHandler);
            }

            // Listen for cart update events to refresh inventory quantity
            if (!this._cartUpdateListenerAdded) {
                console.log('[QuickView] Adding cart:updated event listener');
                this._cartUpdateListenerAdded = true;
                document.addEventListener('cart:updated', this._cartUpdateHandler);
            } else {
                console.log('[QuickView] Updating existing cart:updated handler for new product');
                // Ensure handler is updated even if listener is already added
                document.removeEventListener('cart:updated', this._cartUpdateHandler);
                document.addEventListener('cart:updated', this._cartUpdateHandler);
            }

            const form = this.querySelector('[data-product-form]');

            if (form) {
                console.log('[QuickView] Adding form submit listener');
                
                // Define our form submit handler
                const newSubmitHandler = (event) => {
                    console.log('[QuickView] Form submit event triggered');
                    // Double check that the variant is available before submission
                    const variantSelector = this.querySelector('variant-selector[data-handle="'+product.handle+'"] select');
                    if (variantSelector) {
                        const variantId = variantSelector.value;
                        console.log(`[QuickView] Selected variant: ${variantId}`);
                        const variant = product.variants.find(v => v.id == variantId);
                        
                        if (variant && !variant.available) {
                            // Prevent form submission for sold-out variants
                            console.log(`[QuickView] Preventing form submission for sold-out variant: ${variant.id}`);
                            event.preventDefault();
                            event.stopPropagation();
                            return false;
                        } else {
                            console.log(`[QuickView] Variant ${variantId} is available, allowing form submission`);
                            // Let the default form submission proceed normally
                            // Just close the quickview after a short delay
                            setTimeout(() => {
                                this.close();
                            }, 500);
                        }
                    } else {
                        console.log('[QuickView] No variant selector found');
                    }
                };
                
                // Remove existing listeners if needed
                if (form._quickviewSubmitHandler) {
                    console.log('[QuickView] Removing existing form submit listener');
                    form.removeEventListener('submit', form._quickviewSubmitHandler);
                }
                
                // Store the handler reference on the form element for future cleanup
                form._quickviewSubmitHandler = newSubmitHandler;
                
                // Add our new handler
                form.addEventListener('submit', newSubmitHandler);
                console.log('[QuickView] Form submit handler added (preserved original form)');
                
            } else {
                console.log('[QuickView] No product form found');
            }
        }

        /**
         * Immediately update UI elements that don't need inventory data
         * 
         * @param {object} product Product data.
         * @param {object} variant Variant data.
         * @returns {undefined}
         */
        _immediateUIUpdate(product, variant) {
            if (!variant) {
                console.error('[QuickView] No variant provided for UI update');
                return;
            }
            
            console.log(`[QuickView] Immediate UI update for variant ${variant.id} (${variant.title}), available: ${variant.available}`);
            
            const productAssociation = this.querySelector('product-association');
            let isSizeSelected = false;
            
            if (productAssociation) {
                const allOptions = productAssociation.querySelectorAll('[data-option]');
                for (let option of allOptions) {
                    const optionName = option.getAttribute('data-option');
                    if (optionName && optionName.toLowerCase().includes('size')) {
                        const sizeInput = option.querySelector('input[data-swatch-input]:checked');
                        isSizeSelected = sizeInput !== null;
                        break;
                    }
                }
            }
            
            const sizeNotSelectedBtn = this.querySelector('.js-product-form__buttons-size-selected');
            const sizeSelectedBtn = this.querySelector('.js-product-form__buttons');
            
            if (isSizeSelected) {
                if (sizeNotSelectedBtn) sizeNotSelectedBtn.classList.add('hidden');
                if (sizeSelectedBtn) sizeSelectedBtn.classList.remove('hidden');
            } else {
                if (sizeNotSelectedBtn) sizeNotSelectedBtn.classList.remove('hidden');
                if (sizeSelectedBtn) sizeSelectedBtn.classList.add('hidden');
            }
            
            const addToCartBtn = this.querySelector(".product-addtocart__button");
            if (addToCartBtn && isSizeSelected) {
                if (!variant.available) {
                    addToCartBtn.disabled = true;
                    addToCartBtn.setAttribute('disabled', 'disabled');
                    addToCartBtn.classList.add("disabled");
                } else {
                    addToCartBtn.removeAttribute('disabled');
                    addToCartBtn.classList.remove("disabled");
                }
            } else if (addToCartBtn && !isSizeSelected) {
                addToCartBtn.disabled = true;
                addToCartBtn.setAttribute('disabled', 'disabled');
                addToCartBtn.classList.add("disabled");
            }
            
            // Reset inventory messages
            const inStockItem = this.querySelector('.js-in-stock');
            const inStockAllAddedEl = this.querySelector('.js-in-stock-all-added');
            
            console.log('[QuickView] Resetting inventory messages');
            if (inStockItem) {
                inStockItem.classList.add("hidden");
                console.log('[QuickView] In-stock message hidden');
            } else {
                console.log('[QuickView] In-stock message element not found');
            }
            
            if (inStockAllAddedEl) {
                inStockAllAddedEl.classList.add("hidden");
                console.log('[QuickView] All-added message hidden');
            } else {
                console.log('[QuickView] All-added message element not found');
            }
        }
    }

    customElements.define(customElements.component, quickView);    
}
